import { trans } from "@mongez/localization";
import { createPortal } from "core/shared";
import { useEffect, useState } from "react";
import { Button } from "~/design-system/components";
import { Switch } from "~/design-system/components/ui/switch";
import { Portal } from "~/modules/shared/components";
import { useCookieConsent } from "../hooks";
import { CookieConsent } from "./CookieConsentBanner";

export const cookiePreferencesPortal = createPortal(
  "cookie-preferences-portal",
);

export function CookiePreferencesPortal() {
  const { updateConsent } = useCookieConsent();
  const [preferences, setPreferences] = useState<CookieConsent>({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Load existing preferences
    const savedConsent = localStorage.getItem("cookie_consent");
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(parsed);
      } catch (error) {
        console.error("Error parsing saved cookie consent:", error);
      }
    }
  }, []);

  const handleToggle = (category: keyof CookieConsent) => {
    if (category === "essential") return; // Essential cookies cannot be disabled

    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    updateConsent(preferences);
    cookiePreferencesPortal.close();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookieConsent = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    updateConsent(allAccepted);
    cookiePreferencesPortal.close();
  };

  const handleRejectAll = () => {
    const onlyEssential: CookieConsent = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    updateConsent(onlyEssential);
    cookiePreferencesPortal.close();
  };

  return (
    <Portal
      portal={cookiePreferencesPortal}
      width={900}
      title={trans("cookiePreferences.title") || "Cookie Preferences"}
      subtitle={
        trans("cookiePreferences.subtitle") ||
        "Manage your cookie preferences below. You can enable or disable different types of cookies."
      }
      classes={{
        content: "max-h-[90vh] overflow-y-auto",
      }}
    >
      <div className="space-y-6">
        {/* Cookie Categories */}
        <div className="space-y-6">
          {/* Essential Cookies */}
          <div className="rounded-xl border-l-4 border-l-green-500 bg-green-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-playfair text-xl font-bold text-gray-900">
                  {trans("cookiePreferences.essential.title") ||
                    "Essential Cookies"}
                </h3>
                <p className="mt-1 text-sm font-medium text-gray-600">
                  {trans("cookiePreferences.essential.subtitle") ||
                    "Required for basic website functionality"}
                </p>
              </div>
              <span className="ml-4 rounded-full bg-green-100 px-4 py-1.5 text-sm font-semibold text-green-800">
                {trans("cookiePreferences.alwaysActive") || "Always Active"}
              </span>
            </div>
            <p className="text-sm text-gray-700">
              {trans("cookiePreferences.essential.description") ||
                "These cookies are necessary for the website to function and cannot be switched off. They include language preferences, security settings, and basic functionality."}
            </p>
          </div>

          {/* Analytics Cookies */}
          <div className="rounded-xl border-l-4 border-l-blue-500 bg-blue-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-playfair text-xl font-bold text-gray-900">
                  {trans("cookiePreferences.analytics.title") ||
                    "Analytics Cookies"}
                </h3>
                <p className="mt-1 text-sm font-medium text-gray-600">
                  {trans("cookiePreferences.analytics.subtitle") ||
                    "Help us understand how visitors use our website"}
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={() => handleToggle("analytics")}
                className="ml-4"
              />
            </div>
            <p className="text-sm text-gray-700">
              {trans("cookiePreferences.analytics.description") ||
                "These cookies collect information about how visitors use our website, such as which pages are visited most often. This helps us improve our website's performance."}
            </p>
          </div>

          {/* Functional Cookies */}
          <div className="rounded-xl border-l-4 border-l-purple-500 bg-purple-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-playfair text-xl font-bold text-gray-900">
                  {trans("cookiePreferences.functional.title") ||
                    "Functional Cookies"}
                </h3>
                <p className="mt-1 text-sm font-medium text-gray-600">
                  {trans("cookiePreferences.functional.subtitle") ||
                    "Enable enhanced functionality and personalization"}
                </p>
              </div>
              <Switch
                checked={preferences.functional}
                onCheckedChange={() => handleToggle("functional")}
                className="ml-4"
              />
            </div>
            <p className="text-sm text-gray-700">
              {trans("cookiePreferences.functional.description") ||
                "These cookies enable enhanced functionality and personalization, such as remembering your preferences and providing improved features."}
            </p>
          </div>

          {/* Marketing Cookies */}
          <div className="rounded-xl border-l-4 border-l-orange-500 bg-orange-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-playfair text-xl font-bold text-gray-900">
                  {trans("cookiePreferences.marketing.title") ||
                    "Marketing Cookies"}
                </h3>
                <p className="mt-1 text-sm font-medium text-gray-600">
                  {trans("cookiePreferences.marketing.subtitle") ||
                    "Used to display relevant advertisements"}
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={() => handleToggle("marketing")}
                className="ml-4"
              />
            </div>
            <p className="text-sm text-gray-700">
              {trans("cookiePreferences.marketing.description") ||
                "These cookies are used to track visitors across websites to display relevant and engaging advertisements."}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row">
          <Button
            onClick={handleRejectAll}
            className="flex-1 justify-center rounded-full border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50"
          >
            {trans("cookiePreferences.rejectAll") || "Reject All Non-Essential"}
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="flex-1 justify-center rounded-full border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50"
          >
            {trans("cookiePreferences.acceptAll") || "Accept All"}
          </Button>
          <Button
            onClick={handleSave}
            className="bg-primary-default hover:bg-primary-600 flex-1 justify-center rounded-full font-medium text-white"
          >
            {trans("cookiePreferences.savePreferences") || "Save Preferences"}
          </Button>
        </div>

        {/* Footer Note */}
        <div className="pt-4 text-center">
          <p className="text-sm text-gray-600">
            {trans("cookiePreferences.footerNote") ||
              'You can change your preferences at any time by clicking the "Manage Cookies" link in our website footer.'}
          </p>
        </div>
      </div>
    </Portal>
  );
}
